import React, { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode, useCallback } from 'react';
import { translations } from './translations';
import { TranslationKey } from './translations';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: typeof translations;
  t: (key: string) => string;
  isRTL: boolean;
}

const STORAGE_KEY = 'hudan_language';

const LanguageContext = createContext<LanguageContextType>({
  language: 'ar',
  setLanguage: () => {},
  translations,
  t: () => '',
  isRTL: true,
});

// Get initial language from URL, then storage, defaulting to Arabic
function getInitialLanguage(): Language {
  // ?lang=ar|en — the hreflang alternate URLs emitted by <SEO> point here,
  // so the param must actually switch the language for them to be truthful.
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang === 'ar' || urlLang === 'en') {
    return urlLang;
  }

  // Respect an explicit user choice persisted from a previous visit
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'ar' || stored === 'en') {
    return stored;
  }

  // Default to Arabic regardless of browser language
  return 'ar';
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Function to update document direction and language attribute
  const updateDocumentDirection = useCallback((lang: Language) => {
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    const html = document.documentElement;

    // Set direction and language on html element
    html.setAttribute('dir', dir);
    html.setAttribute('lang', lang);

    // Toggle RTL/LTR classes on both html and body
    html.classList.toggle('rtl', lang === 'ar');
    html.classList.toggle('ltr', lang !== 'ar');
    html.classList.toggle('font-arabic', lang === 'ar');

    document.body.classList.toggle('rtl', lang === 'ar');
    document.body.classList.toggle('ltr', lang !== 'ar');
    document.body.classList.toggle('font-arabic', lang === 'ar');

    // Update font family for Arabic
    if (lang === 'ar') {
      document.body.style.fontFamily = "'Cairo', 'Tajawal', 'Noto Kufi Arabic', 'Noto Sans Arabic', sans-serif";
    } else {
      document.body.style.fontFamily = "'Inter', system-ui, sans-serif";
    }
  }, []);

  // Set language with persistence
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    updateDocumentDirection(lang);
  }, [updateDocumentDirection]);

  // Initialize direction on mount — useLayoutEffect so lang/dir are set
  // synchronously after DOM mutations and before paint, preventing the
  // react-helmet-async deferred RAF from racing with Playwright's
  // networkidle check on data-fetching pages like FAQ.
  useLayoutEffect(() => {
    updateDocumentDirection(language);
  }, [language, updateDocumentDirection]);

  // Function to get a translation by key
  const t = useCallback((key: string): string => {
    if (key in translations[language]) {
      return translations[language][key as TranslationKey];
    }
    // Fallback to English if Arabic translation is missing
    if (language === 'ar' && key in translations['en']) {
      return translations['en'][key as TranslationKey];
    }
    return key;
  }, [language]);

  const isRTL = language === 'ar';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translations, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

// Hook for getting direction-aware CSS classes
export const useDirection = () => {
  const { isRTL } = useLanguage();

  return {
    isRTL,
    // Direction-aware margin/padding
    ms: isRTL ? 'me' : 'ms', // margin-start
    me: isRTL ? 'ms' : 'me', // margin-end
    ps: isRTL ? 'pe' : 'ps', // padding-start
    pe: isRTL ? 'ps' : 'pe', // padding-end
    // Direction-aware positioning
    start: isRTL ? 'right' : 'left',
    end: isRTL ? 'left' : 'right',
    // Direction-aware flex
    flexDir: isRTL ? 'flex-row-reverse' : 'flex-row',
  };
};
